import { faCircleCheck, faCircleX } from "@fortawesome/pro-solid-svg-icons";
import { BasicLinkButton, DeleteLinkButton, EditLinkButton, TextTruncate } from "components";
import EllipsisPopupButton from "components/buttons/ellipsisPopupButton/EllipsisPopupButton";
import { Tooltip } from "components/common/tooltip";
import { Title } from "components/listViewTemplates";
import { TemplateImage } from "components/people/templates/TemplateImage";
import { type ColumnOption } from "interfaces/columnOptions";
import dateTimeUtils from "utils/dateTimeUtils";
import { type TemplateOverview, TemplateStatusEnum } from "../types/models";
import { TemplateStatus } from "./TemplateStatus";

export enum TemplatesOverviewColumns {
  Name = "Name",
  Owner = "Owner",
  Added = "Added",
  Modified = "Modified",
  Customers = "Customers",
  Users = "Users",
  Status = "Status",
}

export const columnToParamMap: {
  [P in Lowercase<TemplatesOverviewColumns>]: string;
} = {
  name: "name",
  owner: "publisher",
  status: "isDraft",
  modified: "dateModified",
  added: "dateCreated",
  customers: "customers",
  users: "users",
};

export const getColumnOptions = ({
  handleEdit,
  handleTitleClick,
  hasManagePermission,
  hasGroupManagePermission,
  processTemplate,
  handleDelete,
  onlyOwn,
  hiddenColumns,
}: {
  handleEdit: (id: number) => void;
  handleTitleClick: (item: TemplateOverview) => string;
  hasManagePermission: boolean;
  hasGroupManagePermission: boolean;
  processTemplate: (id: number, approve: boolean, owner: string) => () => void;
  handleDelete: (id: number) => () => void;
  onlyOwn: boolean;
  hiddenColumns: TemplatesOverviewColumns[];
}): ColumnOption<TemplateOverview>[] => {
  return [
    {
      name: "",
      width: 1,
      isSortable: false,
      render: () => {
        return <TemplateImage imageUrl={""} />;
      },
    },
    {
      name: TemplatesOverviewColumns.Name,
      width: 7,
      isSortable: true,
      render: (item) => {
        const url = handleTitleClick(item);
        return <Title title={item.name} url={url} clamp={2} className="normal-color" />;
      },
    },
    {
      name: TemplatesOverviewColumns.Owner,
      width: 3,
      isHide: onlyOwn,
      isSortable: false,
      render: (item) => <Tooltip target={<TextTruncate>{item.publisher}</TextTruncate>} content={item.publisher} />,
    },
    {
      name: TemplatesOverviewColumns.Added,
      width: 2,
      isSortable: true,
      render: (item) =>
        item.isOwn || item.status === TemplateStatusEnum.Approved ? dateTimeUtils.formatDate(item.dateCreated) : "-",
    },
    {
      name: TemplatesOverviewColumns.Modified,
      width: 2,
      isSortable: true,
      render: (item) =>
        item.isOwn || item.status === TemplateStatusEnum.Approved ? dateTimeUtils.formatDate(item.dateModified) : "-",
    },
    {
      name: TemplatesOverviewColumns.Customers,
      width: 2,
      isSortable: false,
      render: (item) => item.customers,
      isHide: hiddenColumns.includes(TemplatesOverviewColumns.Customers),
    },
    {
      name: TemplatesOverviewColumns.Status,
      width: 3,
      isSortable: false,
      isHide: onlyOwn,
      render: (item) => (item.isOwn ? "-" : <TemplateStatus status={item.status} />),
    },
    {
      name: "",
      width: 1,
      className: "align-right",
      render: (item) => (
        <EllipsisPopupButton circle outlinedEllipsis disabled={!item.isOwn && !item.canEdit}>
          {item.isOwn
            ? [
                <EditLinkButton onClick={() => handleEdit(item.id)} isDisabled={!hasManagePermission} key="edit" />,
                <DeleteLinkButton onClick={handleDelete(item.id)} isDisabled={!hasManagePermission} key="delete" />,
              ]
            : [
                <BasicLinkButton
                  text="Approve"
                  onClick={processTemplate(item.templateGroupId ?? item.id, true, item.publisher)}
                  icon={faCircleCheck}
                  key="approve"
                  isDisabled={!hasGroupManagePermission || item.status === TemplateStatusEnum.Approved}
                />,
                <BasicLinkButton
                  text="Decline"
                  onClick={processTemplate(item.templateGroupId ?? item.id, false, item.publisher)}
                  icon={faCircleX}
                  key="decline"
                  isDisabled={!hasGroupManagePermission || item.status === TemplateStatusEnum.Rejected}
                />,
              ]}
        </EllipsisPopupButton>
      ),
    },
  ];
};
